import { scaleLinear } from "d3";

import { DEFAULT_RANGE_PADDING_FACTOR, MIN_VIEW_RANGE_SIZE, RANGE_PREFETCH_FRACTION } from "@/settings/settings.js";

import { type Contig } from "../graphql.js";
import { type TrackRange } from "../ui-modules/genome-viewer/new/useViewerGraphql.js";

export function rangeEquals(range1: TrackRange, range2: TrackRange): boolean {
  return range1?.start === range2?.start && range1?.stop === range2?.stop;
}

export function rangesIntersect(range1: TrackRange, range2: TrackRange): boolean {
  return (
    rangeContainsValue(range1, range2.start) ||
    rangeContainsValue(range1, range2.stop) ||
    rangeContainsValue(range2, range1.start) ||
    rangeContainsValue(range2, range1.stop)
  );
}

export function rangeContainsValue(range: TrackRange, i: number): boolean {
  return range.start <= i && i <= range.stop;
}

export function rangeContainsRange(outer: TrackRange, inner: TrackRange): boolean {
  return rangeContainsValue(outer, inner.start) && rangeContainsValue(outer, inner.stop);
}

export function restrictRange(range: TrackRange, min: number, max: number): TrackRange {
  return {
    start: Math.max(range.start, min),
    stop: Math.min(range.stop, max),
  };
}

export function roundRange(range: TrackRange): TrackRange {
  return {
    start: Math.round(range.start),
    stop: Math.round(range.stop),
  };
}

/**
 * @return start and stop are integer values, that is, floats with only 0s after the decimal point.
 */
export function ensureSafeContigRange(range: TrackRange, contigLength: number): TrackRange {
  return roundRange(restrictRange(range, 1, contigLength));
}

export function getRangeCenter(range: TrackRange): number {
  // get the middle base position of the old range
  return range.start + Math.floor((range.stop - range.start) / 2);
}

// get the base length of the range (stop is inclusive)
export function getRangeLength(range: TrackRange): number {
  return (range?.stop ?? 0) - (range?.start ?? 0) + 1;
}

// shift a range by a certain amount to the left or to the right
// distance can be positive or negative!
export function shiftRange(range: TrackRange, distance: number): TrackRange {
  // Make a defensive copy instead of in place modification of arguments!
  return {
    start: range.start + distance,
    stop: range.stop + distance,
  };
}

export function getScaledRangeLength(
  oldRange: TrackRange,
  scaleFactor: number,
  minRangeLength: number,
  maxRange: number,
): number {
  let newLength: number;

  newLength = Math.round(getRangeLength(oldRange) * scaleFactor);
  newLength = Math.min(Math.max(newLength, minRangeLength), maxRange);

  return newLength;
}

export function getRangeCenteredAt(oldRange: TrackRange, newLength: number, maxRange: number): TrackRange {
  const newHalfLength: number = Math.floor(newLength / 2);

  let newRange: TrackRange = {
    start: getRangeCenter(oldRange) - newHalfLength,
    stop: getRangeCenter(oldRange) + newHalfLength,
  };

  if (newRange.start < 1) {
    // we need to shift the range to the right (correction is positive)
    const correction: number = 1 - newRange.start;

    newRange = shiftRange(newRange, correction);
  }

  if (newRange.stop > maxRange) {
    // we need to shift the range to the left (correction is negative)
    const correction: number = -1 * (newRange.stop - maxRange);

    newRange = shiftRange(newRange, correction);
  }

  // the range may be out of bounds at the min or the max side - we clip it to the boundaries
  newRange.start = Math.max(newRange.start, 1);
  newRange.stop = Math.min(newRange.stop, maxRange);

  return newRange;
}

export function getPaddedRange(
  range: TrackRange,
  contigLength: number,
  paddingFactor: number = DEFAULT_RANGE_PADDING_FACTOR,
): TrackRange {
  const rangePadding: number = Math.round(paddingFactor * getRangeLength(range));

  // Create result ragne and crop start and end positions as needed
  let resultRange: TrackRange = {
    start: Math.max(range.start - rangePadding, 1),
    stop: Math.min(range.stop + rangePadding, contigLength),
  };

  // Make sure that range length if greater than the global min range length
  if (getRangeLength(resultRange) < MIN_VIEW_RANGE_SIZE) {
    resultRange = getRangeCenteredAt(resultRange, MIN_VIEW_RANGE_SIZE, contigLength);
  }

  return ensureSafeContigRange(resultRange, contigLength);
}

export function clamp(value: number, minValue: number, maxValue: number) {
  return Math.max(Math.min(value, maxValue), minValue);
}

export function getExtendedRange(contig: Contig, range: TrackRange) {
  const rangeLength: number = range.stop - range.start + 1;
  const rangePadding: number = Math.round(rangeLength * RANGE_PREFETCH_FRACTION);

  // The visible range is extended to each side in order to make interactive browsing more seamless
  return ensureSafeContigRange(
    {
      start: range.start - rangePadding,
      stop: range.stop + rangePadding,
    },
    contig.length,
  );
}

// Already apply -1 to the start
const rangeNormalize =
  (width = 0) =>
  (range: TrackRange = { start: 0, stop: 0 }) =>
    scaleLinear()
      .domain([range.start - 1, range.stop])
      .range([0, width]);

/// --- Normalize --- ///

export type Normalize = (begin: number, end: number, filter?: (v: number) => typeof v) => [number, number, number];

function normalizedLength(width: number, start: number, stop: number, filter = (v) => v): Normalize {
  const unitLength = width / (stop - start);

  // @returns [normalizedStart, normalizedStop, normalizedLength]
  return (begin, end) => {
    const retStart = filter((begin - start) * unitLength);
    const retStop = filter((end - start) * unitLength);

    // returned width needs to be aligned with start and stop
    // so filter((end - begin) * unitLength) doesn't work because of some filtering like Math.ceil
    return [retStart, retStop, retStop - retStart];
  };
}

// Already apply -1 to range.start
const rangeNormalizedLength =
  (filter = (v) => v) =>
  (width: number) =>
  ({ start, stop }: TrackRange) =>
    normalizedLength(width, start - 1, stop, filter);

/// --- Export --- ///

export { normalizedLength, rangeNormalize, rangeNormalizedLength };
